import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { AuthGroup } from 'root/src/server/util/awsClients/authApiClient.types';
import { useGroups } from 'root/src/client/v2/auth/hooks/useGroups';
import { useLocalStorage } from 'root/src/client/web/hooks/useLocalStorage';
import { getRedesignTwinPathname } from 'root/src/client/v2/layout/helpers/redesignAbRoutes';
// localStorage flag for internal testing: set `localStorage.setItem('dd-redesign-override', 'true')`
// in the devtools console (then reload) to force the redesign on for yourself without being in the
// `redesignBeta` auth group. `removeItem` reverts to the group-driven behavior. Client-only by design:
// localStorage is unreachable in the server pass, so an override load starts on the legacy page and
// this hook redirects after hydration (a brief legacy frame is expected, acceptable for internal use).
export const REDESIGN_OVERRIDE_STORAGE_KEY = 'dd-redesign-override';
// Redesign A/B test (client fallback). Users in the `redesignBeta` auth group (assigned manually)
// are redirected from a legacy page to its redesign twin. The cohort lives in the JWT -> `groups`
// cookie, read here via `useGroups()`. Mounted once in `_app` so the rule lives in a single place
// and no legacy page is edited.
//
// The no-flash path is the server-side redirect in `_app` getInitialProps (it fires before any HTML
// is sent on a hard load). This hook covers what the server pass cannot: client-side navigation to a
// legacy route, and a cohort change mid-session (the `groups` cookie updates -> `useGroups` re-runs
// -> this redirects without a reload). On those client paths a brief legacy frame is possible.
export const useRedesignAbRedirect = () => {
    const router = useRouter();
    const { groups } = useGroups();
    const [override] = useLocalStorage(REDESIGN_OVERRIDE_STORAGE_KEY, false);
    const isRedesignCohort = groups.includes(AuthGroup.RedesignBeta) || override === true;
    useEffect(() => {
        // Wait for the router to be ready, otherwise `router.query` is empty on dynamic routes and
        // the twin's `[recordId]`/`[id]` segment would not be filled.
        if (!isRedesignCohort || !router.isReady) {
            return;
        }
        const twin = getRedesignTwinPathname(router.pathname);
        if (!twin) {
            return;
        }
        // `replace` (not `push`) so Back does not bounce between legacy and redesign. The twin is not
        // itself a map key, so this never loops. `query` carries the dynamic params + any extra query.
        void router.replace({ pathname: twin, query: router.query });
    }, [isRedesignCohort, router]);
};
